// Customizable Area Start
import React from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Hidden,
} from "@mui/material";
import { fileUploadIcon, supportImage, contactImage } from "./assets";
import { Formik } from "formik";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import NavigationMenu from "../../../blocks/navigationmenu/src/NavigationMenu.web";
import AppFooter from "../../../components/src/AppFooter.web";
import ContactUsController, {
  Props,
  configJSON,
  userSchema,
} from "./ContactusController";
import CustomInfoDialog from "../../../components/src/CustomInfoDialog.web";
import FullPageLoader from "../../../components/src/FullPageLoader.web";
import BannerWithContent from "../../../components/src/BannerWithContent.web";
import GoogleAd from "../../../components/src/GoogleAd.web";
// Customizable Area End

export default class ContactUs extends ContactUsController {
  constructor(props: Props) {
    super(props);
  }
  render() {
    const { isLoading, isGettingCUImage, contactUsImage } = this.state;
    return (
      // Customizable Area Start
      <div>
        <FullPageLoader isLoading={isLoading} />
        <NavigationMenu navigation={this.props.navigation} id={""} />
        <BannerWithContent isOverlap backgroundImage={contactImage}>
          <Formik
            innerRef={this.formikRef}
            data-test-id="formik"
            initialValues={{
              firstName: "",
              lastName: "",
              phoneNumber: "",
              email: "",
              subject: "",
              message: "",
            }}
            validationSchema={userSchema}
            onSubmit={this.handleContactUsSubmit}
            validateOnChange={true}
            validateOnBlur={false}
          >
            {({
              errors,
              touched,
              handleChange,
              setFieldValue,
              handleSubmit,
              values,
            }) => {
              const isFormValid = Object.keys(errors).length === 0;

              return (
                <form onSubmit={handleSubmit} noValidate>
                  <Box sx={modalStyle.formBox}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box>
                          <Typography
                            variant="h4"
                            component="h1"
                            data-test-id="main-heading"
                            sx={modalStyle.heading}
                          >
                            Get in Touch
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={modalStyle.description}
                          >
                            We look forward to hearing from you! You can also
                            contact us using the form below if you need
                            assistance. Please fill out the form and describe
                            your query in as much detail as possible. You'll
                            receive a reply via email, so please make sure to
                            enter your email address correctly.
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography sx={modalStyle.label}>
                              First Name{" "}
                              <span style={{ color: "#ff0000" }}>*</span>
                            </Typography>
                            <TextField
                              data-test-id="input-first-name"
                              fullWidth
                              placeholder="Enter your first name"
                              variant="outlined"
                              type="text"
                              name="firstName"
                              value={values.firstName}
                              inputProps={{ maxLength: 30 }}
                              onChange={handleChange}
                              error={touched.firstName && !!errors.firstName}
                              helperText={
                                touched.firstName && errors.firstName ? (
                                  <span style={{ color: "red" }}>
                                    {errors.firstName}
                                  </span>
                                ) : (
                                  ""
                                )
                              }
                              sx={modalStyle.input}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography sx={modalStyle.label}>
                              Last Name{" "}
                              <span style={{ color: "#ff0000" }}>*</span>
                            </Typography>
                            <TextField
                              data-test-id="input-last-name"
                              fullWidth
                              placeholder="Enter your last name"
                              variant="outlined"
                              type="text"
                              required
                              name="lastName"
                              value={values.lastName}
                              inputProps={{ maxLength: 30 }}
                              onChange={handleChange}
                              error={touched.lastName && !!errors.lastName}
                              helperText={
                                touched.lastName && errors.lastName ? (
                                  <span style={{ color: "red" }}>
                                    {errors.lastName}
                                  </span>
                                ) : (
                                  ""
                                )
                              }
                              sx={modalStyle.input}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography sx={modalStyle.label}>
                              Email Address{" "}
                              <span style={{ color: "#ff0000" }}>*</span>
                            </Typography>
                            <TextField
                              data-test-id="input-email"
                              fullWidth
                              required
                              inputMode="email"
                              placeholder="Enter your email address"
                              variant="outlined"
                              type="email"
                              name="email"
                              value={values.email}
                              inputProps={{ maxLength: 50 }}
                              onChange={handleChange}
                              sx={modalStyle.input}
                              helperText={
                                touched.email && errors.email ? (
                                  <span style={{ color: "red" }}>
                                    {errors.email}
                                  </span>
                                ) : (
                                  ""
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography sx={modalStyle.label}>
                              Mobile Number{" "}
                              <span style={{ color: "#ff0000" }}>*</span>
                            </Typography>

                            <TextField
                              data-test-id="input-phone-number"
                              fullWidth
                              placeholder="Enter your mobile number"
                              variant="outlined"
                              required
                              type="tel"
                              inputMode="numeric"
                              name="phoneNumber"
                              value={values.phoneNumber}
                              onChange={(event) => {
                                const numericValue = event.target.value.replace(
                                  /\D/g,
                                  ""
                                );
                                setFieldValue("phoneNumber", numericValue);
                              }}
                              inputProps={{ maxLength: 10 }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography className="countryCode">
                                      +91
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                              sx={modalStyle.input}
                              helperText={
                                touched.phoneNumber && errors.phoneNumber ? (
                                  <span style={{ color: "red" }}>
                                    {errors.phoneNumber}
                                  </span>
                                ) : (
                                  ""
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography sx={modalStyle.label}>
                              Subject{" "}
                              <span style={{ color: "#ff0000" }}>*</span>
                            </Typography>
                            <TextField
                              data-test-id="input-subject"
                              fullWidth
                              type="text"
                              placeholder="Enter your subject ( 50 characters )"
                              variant="outlined"
                              inputProps={{ maxLength: 50 }}
                              required
                              name="subject"
                              rows={4}
                              multiline
                              value={values.subject}
                              sx={modalStyle.textArea}
                              onChange={handleChange}
                              helperText={
                                touched.subject && errors.subject ? (
                                  <span style={{ color: "red" }}>
                                    {errors.subject}
                                  </span>
                                ) : (
                                  ""
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography sx={modalStyle.label}>
                              Message{" "}
                              <span style={{ color: "#ff0000" }}>*</span>
                            </Typography>
                            <TextField
                              data-test-id="input-message"
                              fullWidth
                              multiline
                              type="text"
                              required
                              placeholder="Enter your message ( 500 characters )"
                              rows={4}
                              name="message"
                              variant="outlined"
                              value={values.message}
                              sx={modalStyle.textArea}
                              inputProps={{ maxLength: 500 }}
                              onChange={handleChange}
                              helperText={
                                touched.message && errors.message ? (
                                  <span style={{ color: "red" }}>
                                    {errors.message}
                                  </span>
                                ) : (
                                  ""
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography sx={modalStyle.attachmentsText}>
                              Attachments (10MB)
                            </Typography>
                            <Box sx={modalStyle.attachmentsPreviewWrapper}>
                              <Box sx={modalStyle.fileBox}>
                                <input
                                  type="file"
                                  multiple
                                  data-test-id="file-upload"
                                  style={{
                                    opacity: 0,
                                    position: "absolute",
                                    width: "100%",
                                    height: "100%",
                                    cursor: "pointer",
                                  }}
                                  onChange={(e) => this.handleFileChange(e)}
                                />
                                <img
                                  src={fileUploadIcon}
                                  alt="plus-icon"
                                  style={{ width: "100px", height: "100px" }}
                                />
                              </Box>
                              {this.state.attachments.map((fileInfo) => {
                                const { id, file } = fileInfo;

                                return (
                                  <Box
                                    key={id}
                                    title={file.name}
                                    sx={modalStyle.attachmentCard}
                                  >
                                    <IconButton
                                      data-test-id="remove-attachment"
                                      sx={modalStyle.removeIcon}
                                      onClick={() =>
                                        this.handleRemoveAttachment(id)
                                      }
                                    >
                                      <CloseRoundedIcon fontSize="small" />
                                    </IconButton>
                                    {this.renderFilePreview(fileInfo)}
                                  </Box>
                                );
                              })}
                            </Box>
                            <Typography
                              data-test-id="file-error"
                              variant="body2"
                              sx={{
                                ...modalStyle.errorText,
                                textAlign: "left",
                              }}
                            >
                              {this.state.fileError}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" mt={1}>
                              <Button
                                fullWidth
                                variant="contained"
                                sx={modalStyle.submitButton}
                                type="submit"
                                data-test-id="submitButton"
                                disabled={!isFormValid}
                              >
                                {configJSON.sendMsgBtnText}
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>

                      {!isGettingCUImage && (
                        <Hidden mdDown>
                          <Grid item md={4}>
                            <Box sx={modalStyle.supportImageContainer}>
                              <Box sx={modalStyle.supportImageWrapper}>
                                <Box
                                  alt="Support"
                                  component="img"
                                  src={contactUsImage || supportImage}
                                  sx={modalStyle.supportImage}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        </Hidden>
                      )}
                    </Grid>
                  </Box>
                </form>
              );
            }}
          </Formik>
          <GoogleAd
            responsive
            format="auto"
            sx={{ mt: 6 }}
            slot="2261089725"
          />
        </BannerWithContent>
        <AppFooter navigation={this.props.navigation} />
        <CustomInfoDialog
          infoType="success"
          isOpen={this.state.isModalOpen}
          title={configJSON.successfulContactUsText}
          handleClose={this.setCloseSuccess}
        />
      </div>
      // Customizable Area End
    );
  }
}
// Customizable Area Start
const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  formBox: {
    position: "relative",
    "@media (max-width: 1000px)": {
      padding: "20px",
    },
    "@media (max-width: 600px)": {
      padding: "10px",
    },
    "@media (max-width: 400px)": {
      padding: "0px",
    },
  },
  heading: {
    fontWeight: 600,
    fontFamily: "Montserrat",
    fontSize: "28px",
    lineHeight: "32px",
    letterSpacing: "-0.12px",
    color: "#0F172A",
    marginBottom: "17px",
    "@media (max-width: 600px)": {
      fontSize: "24px",
      lineHeight: "26px",
    },
  },
  description: {
    marginBottom: "24px",
    fontWeight: 300,
    fontFamily: "Montserrat !important",
    fontSize: "16px",
    lineHeight: "19.5px",
    color: "#64748B",
    "@media (max-width: 600px)": {
      fontSize: "14px",
      lineHeight: "18px",
    },
  },
  label: {
    fontFamily: "Montserrat !important",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "22px",
    color: "#0F172A",
    marginBottom: "4px",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "32px",
      color: "#0F172A",
      "&:hover": { borderColor: "#cbd5e1" },
      "&.Mui-focused": {
        borderColor: "#673ab7",
        boxShadow: "0 0 0 3px rgba(103, 58, 183, 0.2)",
      },
      "& fieldset": {
        borderColor: "#CBD5E1",
      },
    },
  },
  textArea: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "24px",
      height: "",
    },
    "& fieldset": {
      borderColor: "#CBD5E1",
    },
  },
  submitButton: {
    backgroundColor: "#673ab7",
    color: "white",
    padding: 1.5,
    fontSize: "1rem",
    maxWidth: "360px",
    borderRadius: "30px",
    background: "linear-gradient(90deg, #8077FF 0%, #652784 60%)",
    textTransform: "none",
    "&:hover": { backgroundColor: "#512da8" },
    "&.Mui-disabled": {
      color: "#FFFFFF",
      opacity: 0.25,
    },
  },
  errorText: {
    marginTop: "8px",
    textAlign: "center",
    fontSize: "12px",
    overflowWrap: "break-word",
    color: "red",
  },
  attachmentsText: {
    fontFamily: "Montserrat",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "22px",
    color: "#0F172A",
    marginBottom: "4px",
  },
  fileBox: {
    width: "100px",
    height: "100px",
    border: "1px dotted #94A3B8",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    flexDirection: "column",
  },
  attachmentsPreviewWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "11px",
    flexWrap: "wrap",
  },
  attachmentCard: {
    border: "1px solid #94A3B8",
    borderRadius: "6px",
    height: "100px",
    width: "100px",
    padding: "5px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  removeIcon: {
    position: "absolute",
    top: "-9px",
    right: "-9px",
    padding: "2px",
    background: "linear-gradient(180deg, #8077FF 0%, #652784 100%)",
    "& svg": {
      color: "#fff",
      fontSize: "16px",
    },
  },
  supportImageContainer: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    height: "100%",
  },
  supportImageWrapper: {
    width: "100%",
    maxWidth: 360,
    aspectRatio: "3 / 4",
    overflow: "hidden",
  },
  supportImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
};
// Customizable Area End
